import { Button, Checkbox, Stack } from '@mantine/core';
import { schemaResolver, useForm } from '@mantine/form';
import { IconPlus } from '@tabler/icons-react';
import { Panel } from 'components/Panel';
import { SaveButton } from 'components/SaveButton';
import { i18nKeys, useTranslation } from 'locales';
import {
  useLoadTermsAndConditionsSettings,
  useUpdateTermsAndConditionsSettings,
} from 'modules/Settings/Configuration/api';
import { useEffect } from 'react';
import { useCanSendTermsWithInvoices } from 'shared/utils/connector';
import { TermsAndConditionsSetRow } from './TermsAndConditionsSetRow';
import {
  emptySet,
  INITIAL_VALUES,
  TermsAndConditionsFormSchema,
  type TermsAndConditionsFormValues,
  toFormValues,
  toUpdateVariables,
} from './terms-and-conditions-form';

export const TermsAndConditionsCard = () => {
  const { t } = useTranslation();
  const canSendTermsWithInvoices = useCanSendTermsWithInvoices();

  const { termsAndConditionsSettings } = useLoadTermsAndConditionsSettings();
  const { updateTermsAndConditionsSettings, isUpdateTermsAndConditionsSettingsLoading } =
    useUpdateTermsAndConditionsSettings();

  const form = useForm<TermsAndConditionsFormValues>({
    initialValues: INITIAL_VALUES,
    validate: schemaResolver(TermsAndConditionsFormSchema, { sync: true }),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync server-loaded settings into the form only when the fetched data changes, not on every render (the form object identity is unstable)
  useEffect(() => {
    if (!termsAndConditionsSettings) return;

    const values = toFormValues(termsAndConditionsSettings);
    form.setInitialValues(values);
    form.setValues(values);
  }, [termsAndConditionsSettings]);

  const sets = form.getValues().termsAndConditions;

  // Exactly one set is the default one, so selecting a new one clears the rest.
  const handleSetAsDefault = (index: number) =>
    form.setFieldValue(
      'termsAndConditions',
      sets.map((set, position) => ({ ...set, default: position === index })),
    );

  // Parked with the delete button until removing a set gets a confirmation step.
  // const handleDelete = (index: number) => {
  //   const remaining = sets.filter((_, position) => position !== index);
  //   const needsNewDefault = remaining.every(set => !set.default);
  //
  //   form.setFieldValue(
  //     'termsAndConditions',
  //     remaining.map((set, position) => ({
  //       ...set,
  //       default: needsNewDefault ? position === 0 : set.default,
  //     })),
  //   );
  // };

  const handleAdd = () =>
    form.setFieldValue('termsAndConditions', [
      ...sets,
      { ...emptySet(''), default: sets.length === 0 },
    ]);

  const handleSave = () => {
    if (form.validate().hasErrors) return;

    updateTermsAndConditionsSettings(toUpdateVariables(form.getValues()), {
      onSuccess: () => form.resetDirty(),
    });
  };

  return (
    <Panel
      title={t(i18nKeys.SALE_CONDITION)}
      withBorder
      topRight={
        <Button
          variant="default"
          size="compact-sm"
          leftSection={<IconPlus size={15} stroke={1.8} />}
          onClick={handleAdd}
        >
          {t(i18nKeys.SETTINGS.PREFERENCES.TERMS_AND_CONDITIONS.ADD_SET)}
        </Button>
      }
      footer={
        <SaveButton
          onClick={handleSave}
          disabled={!form.isDirty()}
          loading={isUpdateTermsAndConditionsSettingsLoading}
          hideIcon
        />
      }
    >
      <Stack my="md">
        {sets.map((set, index) => (
          <TermsAndConditionsSetRow
            key={set.id ?? `draft-${index}`}
            form={form}
            index={index}
            onSetAsDefault={() => handleSetAsDefault(index)}
            canSetAsDefault={sets.length > 1 && !set.default}
          />
        ))}

        {canSendTermsWithInvoices && (
          <Checkbox
            label={t(i18nKeys.FORM.PREFERENCES.SEND_CONDITION_EACH_INVOICE)}
            description={t(i18nKeys.FORM.PREFERENCES.TOOLTIP.SEND_CONDITION_EACH_INVOICE)}
            {...form.getInputProps('sendTOS', { type: 'checkbox' })}
          />
        )}
        <Checkbox
          label={t(i18nKeys.FORM.PREFERENCES.MARK_PAID_ON_NO_BALANCE)}
          description={t(i18nKeys.FORM.PREFERENCES.MARK_PAID_ON_NO_BALANCE_TOOLTIP)}
          {...form.getInputProps('ignoreLateFees', { type: 'checkbox' })}
        />
      </Stack>
    </Panel>
  );
};
